import React, { memo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { ASSET_URI } from "../lib/utils";

const fadeIn = {
  hidden: {
    opacity: 0,
    y: 50,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeInOut",
    },
  },
};

const normalizeHexColor = (value = "") => {
  const trimmedValue = String(value || "").trim();

  if (!trimmedValue) {
    return "";
  }

  const withHash = trimmedValue.startsWith("#")
    ? trimmedValue
    : `#${trimmedValue}`;

  return /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(withHash) ? withHash : "";
};

const hexToRgb = (value = "") => {
  const normalized = value.trim().replace("#", "");

  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((character) => `${character}${character}`)
          .join("")
      : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
};

const getCategoryTheme = (value) => {
  const fallback = {
    backgroundColor: "#E0E7FF",
    textColor: "#1E293B",
    metaColor: "#334155",
    imageSurface: "rgba(255, 255, 255, 0.9)",
  };

  const normalizedHexColor = normalizeHexColor(value);

  if (!normalizedHexColor) {
    return fallback;
  }

  const { r, g, b } = hexToRgb(normalizedHexColor);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  const isLightColor = brightness > 155;

  return {
    backgroundColor: normalizedHexColor,
    textColor: isLightColor ? "#0F172A" : "#FFFFFF",
    metaColor: isLightColor ? "#334155" : "rgba(255, 255, 255, 0.82)",
    imageSurface: isLightColor
      ? "rgba(255, 255, 255, 0.88)"
      : "rgba(255, 255, 255, 0.16)",
  };
};

const languageLocales = {
  en: "en-US",
  fa: "fa-AF",
  da: "fa-AF",
  ps: "ps-AF",
  ar: "ar",
};

const CategoryCard = memo(({ category }) => {
  const { i18n, t } = useTranslation();
  const controls = useAnimation();

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  const theme = getCategoryTheme(category?.color);

  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en")
    .split("-")[0]
    .toLowerCase();

  const locale = languageLocales[currentLanguage] || "en-US";

  const categoryName =
    category?.name?.[currentLanguage] ||
    category?.name?.en ||
    category?.name?.fa ||
    category?.name?.ps ||
    t("unnamedCategory", "Unnamed category");

  const courseCount = Number(category?.courseCount || 0);

  useEffect(() => {
    controls.start(inView ? "visible" : "hidden");
  }, [inView, controls]);

  return (
    <Link
      to={`/courses?category=${encodeURIComponent(category?._id || "")}`}
      className="block"
      aria-label={categoryName}
    >
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={fadeIn}
        className="flex flex-row items-center justify-start h-full p-2 overflow-hidden text-left transition-shadow duration-200 rounded shadow-xl lg:p-3 group hover:shadow-2xl"
        style={{
          backgroundColor: theme.backgroundColor,
        }}
      >
        <div
          className="flex-shrink-0 p-0.5 rounded-sm shadow-sm"
          style={{
            backgroundColor: theme.imageSurface,
          }}
        >
          <img
            src={`${ASSET_URI}/${category?.image}`}
            alt={categoryName}
            width="56"
            height="56"
            className="object-cover w-14 h-14"
            loading="lazy"
            crossOrigin="anonymous"
          />
        </div>

        <div className="mx-4 min-w-0">
          <p
            className="mb-1 font-bold truncate ltr:text-left rtl:text-right"
            style={{
              color: theme.textColor,
            }}
          >
            {categoryName}
          </p>

          <p
            className="text-sm leading-5 ltr:text-left rtl:text-right"
            style={{
              color: theme.metaColor,
            }}
          >
            {courseCount.toLocaleString(locale)}{" "}
            {t("courses", {
              count: courseCount,
              defaultValue: "Courses",
            })}
          </p>
        </div>
      </motion.div>
    </Link>
  );
});

CategoryCard.displayName = "CategoryCard";

export default CategoryCard;
